import React, { useState, useRef, useEffect } from 'react';
import './SimpleAutocomplete.css';

interface SimpleAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  className?: string;
}

const SimpleAutocomplete: React.FC<SimpleAutocompleteProps> = ({
  value,
  onChange,
  options,
  placeholder = '',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(''); // Independent search state
  const [filteredOptions, setFilteredOptions] = useState<string[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      const filtered = searchValue === ''
        ? options // Show all options when no search
        : options.filter(option =>
            option.toLowerCase().includes(searchValue.toLowerCase())
          ); // Show all filtered results

      setFilteredOptions(filtered);
      setHighlightedIndex(-1);
    }
  }, [searchValue, options, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchValue(newValue); // Update search value instead of selected value
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    setSearchValue(''); // Clear search when focusing
    setIsOpen(true);
  };

  const handleInputClick = () => {
    setSearchValue(''); // Clear search when clicking
    setIsOpen(true);
  };

  const handleOptionSelect = (option: string) => {
    // Don't select header options (Pokemon names)
    if (option.startsWith('---') && option.endsWith('---')) {
      return;
    }
    onChange(option);
    setSearchValue(''); // Clear search after selection
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown') {
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        // Find next selectable option
        let nextIndex = highlightedIndex + 1;
        while (nextIndex < filteredOptions.length) {
          const option = filteredOptions[nextIndex];
          if (!(option.startsWith('---') && option.endsWith('---'))) {
            setHighlightedIndex(nextIndex);
            break;
          }
          nextIndex++;
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        // Find previous selectable option
        let prevIndex = highlightedIndex > 0 ? highlightedIndex - 1 : filteredOptions.length - 1;
        while (prevIndex >= 0) {
          const option = filteredOptions[prevIndex];
          if (!(option.startsWith('---') && option.endsWith('---'))) {
            setHighlightedIndex(prevIndex);
            break;
          }
          prevIndex--;
        }
        if (prevIndex < 0) setHighlightedIndex(-1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleOptionSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchValue(''); // Clear search on escape
        setHighlightedIndex(-1);
        break;
    }
  };

  return (
    <div className={`simple-autocomplete ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={isOpen ? searchValue : value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onClick={handleInputClick}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="autocomplete-input"
      />

      {isOpen && filteredOptions.length > 0 && (
        <div ref={dropdownRef} className="autocomplete-dropdown">
          {filteredOptions.map((option, index) => {
            const isHeader = option.startsWith('---') && option.endsWith('---');
            return (
              <div
                key={option}
                className={`autocomplete-option ${
                  index === highlightedIndex ? 'highlighted' : ''
                } ${isHeader ? 'header-option' : ''}`}
                onClick={() => handleOptionSelect(option)}
                onMouseEnter={() => {
                  if (!isHeader) {
                    setHighlightedIndex(index);
                  }
                }}
              >
                {option}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SimpleAutocomplete;
